import React from 'react';
import type { WebcamViewControls } from '../components/program/ProgramSlideWebcamDrawOverlay';
import type { WebcamViewTransform } from '../utils/programSlideWebcamDrawExport';

const WEBCAM_VIEW_LS_KEY = 'cp.programSlide.webcamView.v1';
const WEBCAM_VIEW_ZOOM_MIN = 1;
const WEBCAM_VIEW_ZOOM_MAX = 3;
const WEBCAM_VIEW_ZOOM_STEP = 0.25;

function clampWebcamViewZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return 1;
  const stepped = Math.round(zoom / WEBCAM_VIEW_ZOOM_STEP) * WEBCAM_VIEW_ZOOM_STEP;
  return Math.min(WEBCAM_VIEW_ZOOM_MAX, Math.max(WEBCAM_VIEW_ZOOM_MIN, stepped));
}

function readStoredWebcamView(): WebcamViewTransform {
  try {
    const raw = window.localStorage.getItem(WEBCAM_VIEW_LS_KEY);
    if (!raw) return { zoom: 1, flipY: false, flipX: false };
    const parsed = JSON.parse(raw) as Partial<WebcamViewTransform> | null;
    return {
      zoom: clampWebcamViewZoom(Number(parsed?.zoom ?? 1)),
      flipY: parsed?.flipY === true,
      flipX: parsed?.flipX === true,
    };
  } catch {
    return { zoom: 1, flipY: false, flipX: false };
  }
}

/** 웹캠 미리보기 확대·상하/좌우반전 상태(LS 유지)와 툴바 컨트롤, video/img에 줄 스타일 */
export function useWebcamViewTransform(getVideoTrack?: () => MediaStreamTrack | null) {
  const getTrackRef = React.useRef(getVideoTrack);
  getTrackRef.current = getVideoTrack;
  const stableGetTrack = React.useCallback(() => getTrackRef.current?.() ?? null, []);
  const hasTrackGetter = !!getVideoTrack;
  const [view, setView] = React.useState<WebcamViewTransform>(readStoredWebcamView);

  React.useEffect(() => {
    try {
      window.localStorage.setItem(WEBCAM_VIEW_LS_KEY, JSON.stringify(view));
    } catch {
      /* ignore */
    }
  }, [view]);

  const controls = React.useMemo<WebcamViewControls>(
    () => ({
      zoom: view.zoom,
      flipY: view.flipY,
      flipX: !!view.flipX,
      canZoomIn: view.zoom < WEBCAM_VIEW_ZOOM_MAX - 1e-6,
      canZoomOut: view.zoom > WEBCAM_VIEW_ZOOM_MIN + 1e-6,
      onZoomIn: () => setView((v) => ({ ...v, zoom: clampWebcamViewZoom(v.zoom + WEBCAM_VIEW_ZOOM_STEP) })),
      onZoomOut: () => setView((v) => ({ ...v, zoom: clampWebcamViewZoom(v.zoom - WEBCAM_VIEW_ZOOM_STEP) })),
      onZoomReset: () => setView((v) => ({ ...v, zoom: 1 })),
      onToggleFlipY: () => setView((v) => ({ ...v, flipY: !v.flipY })),
      onToggleFlipX: () => setView((v) => ({ ...v, flipX: !v.flipX })),
      ...(hasTrackGetter ? { getVideoTrack: stableGetTrack } : {}),
    }),
    [view, hasTrackGetter, stableGetTrack]
  );

  const transformed = view.zoom !== 1 || view.flipY || !!view.flipX;
  const style = React.useMemo<React.CSSProperties | undefined>(
    () =>
      transformed
        ? ({
            '--cp-webcam-view-transform': `scale(${view.flipX ? -view.zoom : view.zoom}, ${view.flipY ? -view.zoom : view.zoom})`,
          } as React.CSSProperties)
        : undefined,
    [transformed, view]
  );

  return { view, controls, transformed, style };
}
